/**
 * How the document running this code came about, as far as Chrome's prerenderer is concerned.
 *
 * Funnels are client-rendered (`ssr = false`), so a prerendered load hydrates the full bundle
 * and runs the normal boot path: `identify`, `Funnel Loaded` and `Page Viewed` all go out
 * during hydration, and Chrome does not defer network requests while prerendering. Nothing in
 * the event payload distinguishes that from a visit someone actually looked at, which is why
 * the share is unobservable today.
 *
 * This is the measurement half of CAP-4420 and nothing more — it stamps one property on an
 * event we already send. It suppresses nothing and defers nothing; deciding whether to hold
 * events until activation is a separate question that this is meant to inform.
 */
export type PrerenderState =
    /** A normal navigation. This document was never prerendered. */
    | 'none'
    /**
     * The event was emitted while the document was still prerendering. Whether it went on to
     * be activated is unknown at emit time — a prerender that is discarded was seen by
     * nobody, and Chrome discards a large share of what it prerenders.
     */
    | 'prerendering'
    /**
     * The document was prerendered and had already been activated when the event fired, so a
     * real visitor did look at it. Fast activations can land here rather than under
     * `prerendering`, since hydration and activation race.
     */
    | 'activated';

/** Neither API is in TypeScript's `lib.dom` yet (TS 5.9), so both are read off a cast. */
type PrerenderingDocument = Document & { prerendering?: boolean };
type PrerenderAwareNavigationTiming = PerformanceNavigationTiming & { activationStart?: number };

/**
 * Non-zero only for a document that was prerendered and has since been activated: it is the
 * offset from the start of prerendering to the moment the visitor actually navigated.
 */
const wasActivated = (): boolean => {
    const [navigation] = performance.getEntriesByType(
        'navigation',
    ) as PrerenderAwareNavigationTiming[];

    return (navigation?.activationStart ?? 0) > 0;
};

export const getPrerenderState = (): PrerenderState => {
    // Both APIs are Chromium-only, and `performance.getEntriesByType` is absent in some
    // in-app browsers. A load we cannot classify is reported as a normal one rather than
    // taking the funnel down over a measurement.
    try {
        if ((document as PrerenderingDocument).prerendering) {
            return 'prerendering';
        }

        return wasActivated() ? 'activated' : 'none';
    } catch {
        return 'none';
    }
};
